import React, { useMemo } from 'react';

import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Avatar, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

import { levelColor } from '@/features/onboarding/utils/player-card';

import type { HeaderSummaryPlayer } from '@/features/guardian-player/components/player-detail/types';

type HeaderSummaryProps = {
  player: HeaderSummaryPlayer;
};

export default function HeaderSummary({ player }: HeaderSummaryProps) {
  const { name, age, level, avatarUrl } = player;
  const styles = useStyles();
  const { scale } = useScaling();

  const avatarSource = useMemo(() => (avatarUrl ? { uri: avatarUrl } : imgUser), [avatarUrl]);
  const levelBadgeColor = levelColor(level);

  return (
    <Stack align="center" gap={scale(8)}>
      <Stack style={styles.avatarWrapper}>
        <Avatar size={scale(80)} borderWidth={scale(2)} borderColor={colors['0']}>
          <Avatar.Image source={avatarSource} backgroundColor={guardianColors['2']} />
        </Avatar>
      </Stack>
      <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.name}>
        {name}
      </Text>
      <Stack direction="row" align="center" gap={scale(8)}>
        <Stack style={[styles.levelPill, { backgroundColor: levelBadgeColor }]}>
          <Text variant="Font Inter/Subtitle/Bold 10px" style={styles.levelPillText}>
            {STRINGS.guardian.home.levelBadge.replace('{level}', String(level))}
          </Text>
        </Stack>
        {age !== null ? (
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.ageText}>
            {age}{' '}
            {age === 1 ? STRINGS.player.profile.ageYearOld : STRINGS.player.profile.ageYearsOld}
          </Text>
        ) : null}
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  avatarWrapper: {
    position: 'relative',
  },
  cameraBadge: {
    position: 'absolute',
    right: -scale(4),
    bottom: -scale(4),
    width: scale(24),
    height: scale(24),
    borderRadius: scale(12),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.9)',
  },
  pressed: {
    opacity: 0.72,
  },
  name: {
    color: guardianColors['10'],
  },
  levelPill: {
    height: scale(20),
    paddingHorizontal: scale(8),
    borderRadius: scale(10),
    alignItems: 'center',
    justifyContent: 'center',
  },
  levelPillText: {
    color: colors['0'],
  },
  ageText: {
    color: guardianColors['10'],
  },
}));
